import React from "react";
import { Link } from "react-router-dom";
import {
  EnvelopeSimple, Phone, MapPin,
  LinkedinLogo,
} from "@phosphor-icons/react";
import { openCookieSettings } from "../lib/consent";

const SOCIALS = [
  { href: "https://www.linkedin.com/company/cambridge-cardiovascular-prevention-clinic", label: "LinkedIn", icon: LinkedinLogo, key: "linkedin" },
];

const QUICK_LINKS = [
  { to: "/about", label: "About the clinic" },
  { to: "/consultants", label: "Consultants" },
  { to: "/services", label: "Services" },
  { to: "/patient-info", label: "First visit" },
  { to: "/referrals", label: "Self-referral & GP referrals" },
  { to: "/pricing", label: "Fees" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-ink text-cream-100 mt-24">
      <div className="container-ccc py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Brand */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo-mark.png"
              alt="Cambridge Cardiovascular Prevention Clinic logo"
              className="h-12 w-auto brightness-0 invert opacity-95"
            />
            <div className="font-serif text-lg leading-tight">
              Cambridge Cardiovascular<br />Prevention Clinic
            </div>
          </div>
          <p className="mt-5 text-sm text-cream-100/70 leading-relaxed max-w-md">
            Personalised cardiovascular prevention — consultant-led, affiliated with Cambridge University Hospitals.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map(({ href, label, icon: Icon, key }) => (
              <a
                key={key}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-social-${key}`}
                className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-cream-100/20 hover:border-bronze-light hover:text-bronze-light transition-colors"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick links */}
        <div className="lg:col-span-3">
          <div className="eyebrow text-bronze-light">Explore</div>
          <ul className="mt-5 space-y-2.5 text-sm">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.to.replace(/\//g, "")}`}
                  className="text-cream-100/85 hover:text-bronze-light transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="lg:col-span-4">
          <div className="eyebrow text-bronze-light">Visit & contact</div>
          <ul className="mt-5 space-y-3 text-sm text-cream-100/85">
            <li className="flex items-start gap-3">
              <MapPin size={18} weight="duotone" className="mt-0.5 text-bronze-light shrink-0" />
              <span>ACCI Building, Cambridge University Hospitals,<br />Hills Road, Cambridge CB2 0QQ</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={18} weight="duotone" className="text-bronze-light shrink-0" />
              <a href="tel:+441223256653" className="hover:text-bronze-light">+44 (0) 1223 256653</a>
            </li>
            <li className="flex items-center gap-3">
              <EnvelopeSimple size={18} weight="duotone" className="text-bronze-light shrink-0" />
              <a href="mailto:enquiries@camcpc.co.uk" className="hover:text-bronze-light break-all">
                enquiries@camcpc.co.uk
              </a>
            </li>
          </ul>
          <Link
            data-testid="footer-book-cta"
            to="/enquiry"
            className="inline-flex items-center justify-center mt-6 bg-bronze-dark hover:bg-bronze text-white text-sm font-medium tracking-wide rounded-sm px-5 py-3 transition-colors"
          >
            Book an appointment →
          </Link>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-cream-100/10">
        <div className="container-ccc py-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-cream-100/70">
          <div>
            © {new Date().getFullYear()} Cambridge Cardiovascular Clinic Ltd · Company No. 15240297
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-bronze-light">Privacy notice</Link>
            <Link data-testid="footer-terms-link" to="/terms" className="hover:text-bronze-light">Terms</Link>
            <Link data-testid="footer-cookies-link" to="/cookies" className="hover:text-bronze-light">Cookie policy</Link>
            <Link data-testid="footer-complaints-link" to="/complaints" className="hover:text-bronze-light">Complaints</Link>
            <button type="button" data-testid="footer-cookie-settings" onClick={openCookieSettings} className="hover:text-bronze-light underline underline-offset-2">Cookie settings</button>
          </div>
        </div>
        <div className="container-ccc pb-5">
          <p data-testid="footer-cuh-notice" className="text-[11px] text-cream-100/70 leading-relaxed mb-2">
            Consultations take place at Cambridge University Hospitals NHS Foundation Trust.
          </p>
          <p data-testid="footer-legal-notice" className="text-[11px] text-cream-100/60 leading-relaxed">
            Cambridge Cardiovascular Clinic Ltd, registered in England and Wales, company number 15240297, registered office 20 Wenlock Road, London, England, N1 7GU. ICO registration number ZC286200. Trading as Cambridge Cardiovascular Prevention Clinic.
          </p>
        </div>
      </div>
    </footer>
  );
}
